// Bank, Hospital, Godfather and leaderboards.
import { useState } from 'react';
import { api } from '../api.ts';
import { useGame, money, fmt, CLASS_INFO } from '../state.tsx';
import { Loading, useLoad, PlayerLink, Panel, go, PrestigeStar } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';

export function BankPage() {
  const { me, act, content } = useGame();
  const [dep, setDep] = useState('');
  const [wd, setWd] = useState('');
  if (!me) return <Loading />;
  const fee = content.rules.depositFeePct;
  const n = (s: string) => Math.floor(Number(s.replace(/[^0-9]/g, '')));
  return (
    <div>
      <h1 className="page-title">Bank</h1>
      <p className="muted">Cash on hand can be taken by anyone who beats you in a fight. Money in the bank is safe, but the bank keeps a {fee}% fee on deposits.</p>
      <div className="row">
        <Panel title="Deposit">
          <div>Cash on hand: <span className="money">{money(me.cash)}</span></div>
          <form style={{ marginTop: 6 }} onSubmit={async (e) => { e.preventDefault(); const r = await act('/api/bank/deposit', { amount: n(dep) }, { success: (x) => ({ kind: 'good', text: <>Deposited <span className="money">{money(x.deposited)}</span> (fee {money(x.fee)}).</> }) }); if (r) setDep(''); }}>
            $ <input value={dep} onChange={(e) => setDep(e.target.value)} style={{ width: 140 }} /> <button className="btn">Deposit</button>{' '}
            <a className="small" onClick={() => setDep(String(me.cash))}>all</a>
          </form>
          {n(dep) > 0 && <div className="small muted">You will receive {money(n(dep) - Math.ceil((n(dep) * fee) / 100))} after the fee.</div>}
        </Panel>
        <Panel title="Withdraw">
          <div>Bank balance: <span className="money">{money(me.bank)}</span></div>
          <form style={{ marginTop: 6 }} onSubmit={async (e) => { e.preventDefault(); const r = await act('/api/bank/withdraw', { amount: n(wd) }, { success: (x) => ({ kind: 'good', text: <>Withdrew <span className="money">{money(x.withdrawn)}</span>.</> }) }); if (r) setWd(''); }}>
            $ <input value={wd} onChange={(e) => setWd(e.target.value)} style={{ width: 140 }} /> <button className="btn">Withdraw</button>{' '}
            <a className="small" onClick={() => setWd(String(me.bank))}>all</a>
          </form>
        </Panel>
      </div>
    </div>
  );
}

export function HospitalPage() {
  const { me, act, content } = useGame();
  if (!me) return <Loading />;
  const hurt = me.health < me.maxHealth;
  return (
    <div>
      <h1 className="page-title">Hospital</h1>
      <Panel title="Get patched up">
        <p>Your health: <b className={me.health < content.rules.minHealthToAttack ? 'bad' : ''}>{me.health}/{me.maxHealth}</b>.
          {me.health < content.rules.minHealthToAttack && <> You need at least {content.rules.minHealthToAttack} health to fight, and other players leave you alone below that.</>}
        </p>
        {hurt ? (
          <p>The doctor will heal you to full for <span className="money">{money(me.healCost)}</span> (paid from cash on hand first, then your bank).{' '}
            <button className="btn green" onClick={() => act('/api/hospital/heal', {}, { success: (r) => ({ kind: 'good', text: <>You were healed for <span className="money">{money(r.cost)}</span>.</> }) })}>Heal</button>
          </p>
        ) : <p className="good">You’re in perfect health.</p>}
        <p className="small muted">Health also comes back on its own over time, and refills completely when you level up.</p>
      </Panel>
    </div>
  );
}

export function GodfatherPage() {
  const { me, act, content } = useGame();
  const [cls, setCls] = useState('');
  if (!me) return <Loading />;
  const f = content.rules.favor;
  const offers: [string, string, string, number][] = [
    ['refill_energy', 'Refill your energy', 'Get back to work right now.', f.refillEnergyCost],
    ['refill_stamina', 'Refill your stamina', 'More fights, right now.', f.refillStaminaCost],
    ['heal', 'Free hospital visit', 'Heal to full without paying the doctor.', f.healCost],
    ['hire_guns', `Hire ${f.hireGunsAmount} hired guns`, 'They join your mafia permanently.', f.hireGunsCost],
  ];
  return (
    <div>
      <h1 className="page-title">The Godfather</h1>
      <p className="muted">The Godfather trades favors. You earn favor points by leveling up and earning achievements. You have <b className="gold" style={{ fontSize: 14 }}>{me.favorPoints}</b> favor points.</p>
      <table className="list">
        <tbody>
          {offers.map(([action, title, desc, cost]) => (
            <tr key={action}>
              <td><div className="job-name">{title}</div><div className="small muted">{desc}</div></td>
              <td className="gold">{cost} favor points</td>
              <td className="right"><button className={`btn ${me.favorPoints >= cost ? '' : 'disabled'}`} onClick={() => act('/api/godfather', { action }, { success: () => ({ kind: 'good', text: 'The Godfather has granted your request.' }) })}>Ask</button></td>
            </tr>
          ))}
          <tr>
            <td><div className="job-name">Change your class</div><div className="small muted">Currently {CLASS_INFO[me.charClass]?.name}. {' '}
              <select value={cls} onChange={(e) => setCls(e.target.value)}>
                <option value="">choose…</option>
                {Object.entries(CLASS_INFO).filter(([id]) => id !== me.charClass).map(([id, c]) => <option key={id} value={id}>{c.name}: {c.perk}</option>)}
              </select></div></td>
            <td className="gold">{f.classChangeCost} favor points</td>
            <td className="right"><button className={`btn ${me.favorPoints >= f.classChangeCost && cls ? '' : 'disabled'}`} onClick={() => act('/api/godfather', { action: 'class_change', charClass: cls }, { success: () => ({ kind: 'good', text: `You are now a ${CLASS_INFO[cls].name}.` }) })}>Ask</button></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const BOARDS: [string, string, (v: number) => string][] = [
  ['level', 'Experience', (v) => `${fmt(v)} XP`],
  ['fights', 'Fights Won', fmt],
  ['wealth', 'Net Worth', money],
  ['richest', 'Richest', money],
  ['earners', 'Top Earners', money],
  ['jobs', 'Jobs Done', fmt],
  ['mafia', 'Mafia Size', fmt],
  ['robbers', 'Top Robbers', fmt],
  ['bounties', 'Bounties Collected', fmt],
  ['casino', 'Casino Winnings', money],
  ['bosses', 'Bosses Beaten', fmt],
  ['heists', 'Heists', fmt],
];

function readPref(key: string, def: string): string {
  try {
    return localStorage.getItem(key) ?? def;
  } catch {
    return def;
  }
}
function writePref(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: fine */
  }
}

export function LeaderboardPage({ board = 'level' }: { board?: string }) {
  const { me } = useGame();
  const [period, setPeriod] = useState(() => readPref('mm_board_period', 'all'));
  const [humans, setHumans] = useState(() => readPref('mm_board_humans', '0') === '1');
  const { data } = useLoad(() => api.get(`/api/leaderboard?board=${board}&period=${period}&humans=${humans ? 1 : 0}`), [board, period, humans]);
  const def = BOARDS.find((b) => b[0] === board) ?? BOARDS[0];
  const label = data?.weekly ? (board === 'level' ? 'XP this week' : `${def[1]} this week`) : board === 'richest' ? 'Cash + bank' : def[1];
  return (
    <div>
      <h1 className="page-title">Top Mafia</h1>
      <div className="subnav board-tabs" style={{ marginBottom: 8 }}>
        {BOARDS.map(([id, name]) => <a key={id} className={id === board ? 'active' : ''} onClick={() => go(`leaderboard/${id}`)}>{name}</a>)}
      </div>
      <div className="row" style={{ alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <span className="toggle">
          <a className={period === 'all' ? 'on' : ''} onClick={() => { setPeriod('all'); writePref('mm_board_period', 'all'); }}>All time</a>
          <a className={period === 'week' ? 'on' : ''} onClick={() => { setPeriod('week'); writePref('mm_board_period', 'week'); }}>This week</a>
        </span>
        <label className="small"><input type="checkbox" checked={humans} onChange={(e) => { setHumans(e.target.checked); writePref('mm_board_humans', e.target.checked ? '1' : '0'); }} /> Real players only</label>
        {data && period === 'week' && !data.hasWeekly && <span className="small dim">This board has no weekly version; showing all time.</span>}
        {data?.weekly && <span className="small dim">Resets every Monday.</span>}
      </div>
      {!data ? <Loading /> : data.rows.length === 0 ? <div className="muted">Nobody on this board yet{data.weekly ? ' this week' : ''}.</div> : (
        <table className="list">
          <thead><tr><th>#</th><th></th><th>Name</th><th>Level</th><th className="right">{label}</th></tr></thead>
          <tbody>
            {data.rows.map((r: any, i: number) => (
              <tr key={r.id} style={r.id === me?.id ? { outline: '1px solid #b8860b' } : undefined}>
                <td className="gold" style={{ width: 30 }}><b>{i + 1}</b></td>
                <td style={{ width: 36 }}><Portrait seed={r.portrait} playerId={r.id} size={28} /></td>
                <td><PlayerLink id={r.id} name={r.name} />{r.prestige > 0 && <> <PrestigeStar rank={r.prestige} size={15} /></>}{!r.isAi && !humans && <span className="human-tag" title="Real player">★</span>}</td>
                <td>{r.level}</td>
                <td className="right"><b className={r.value < 0 ? 'bad' : ''}>{def[2](r.value)}</b></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
